import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTransactions } from '../contexts/TransactionContext';
import { useBudgets } from '../contexts/BudgetContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { format, subMonths, startOfMonth, endOfMonth, differenceInDays } from 'date-fns';
import { 
  ArrowLeft, 
  Lightbulb, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle, 
  Target,
  Calendar,
  Sparkles,
  PiggyBank,
  ShoppingCart,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Insights = () => {
  const navigate = useNavigate();
  const { allTransactions, selectedMonth } = useTransactions();
  const { budgets } = useBudgets();
  const { theme, isDark } = useTheme();
  const { formatCurrency } = useCurrency();

  // Current month transactions
  const currentMonthTransactions = useMemo(() => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    return allTransactions.filter(t => {
      const transactionDate = t.date;
      return transactionDate >= monthStart && transactionDate <= monthEnd;
    });
  }, [allTransactions, selectedMonth]);

  // Previous month transactions
  const previousMonthTransactions = useMemo(() => {
    const prevMonth = subMonths(selectedMonth, 1);
    const monthStart = startOfMonth(prevMonth);
    const monthEnd = endOfMonth(prevMonth);
    return allTransactions.filter(t => {
      const transactionDate = t.date;
      return transactionDate >= monthStart && transactionDate <= monthEnd;
    });
  }, [allTransactions, selectedMonth]);

  // Two months ago transactions (for trend analysis)
  const twoMonthsAgoTransactions = useMemo(() => {
    const twoMonthsAgo = subMonths(selectedMonth, 2);
    const monthStart = startOfMonth(twoMonthsAgo);
    const monthEnd = endOfMonth(twoMonthsAgo);
    return allTransactions.filter(t => {
      const transactionDate = t.date;
      return transactionDate >= monthStart && transactionDate <= monthEnd;
    });
  }, [allTransactions, selectedMonth]);

  // Generate comprehensive insights
  const insights = useMemo(() => {
    const insightsList = [];
    
    // Calculate category totals for each period
    const getCategoryTotals = (transactions) => {
      const totals = {};
      transactions.filter(t => t.type === 'expense').forEach(t => {
        totals[t.category] = (totals[t.category] || 0) + t.amount;
      });
      return totals;
    };

    const currentTotals = getCategoryTotals(currentMonthTransactions);
    const prevTotals = getCategoryTotals(previousMonthTransactions);
    const twoMonthsAgoTotals = getCategoryTotals(twoMonthsAgoTransactions);

    const currentTotal = Object.values(currentTotals).reduce((a, b) => a + b, 0);
    const prevTotal = Object.values(prevTotals).reduce((a, b) => a + b, 0);
    const twoMonthsAgoTotal = Object.values(twoMonthsAgoTotals).reduce((a, b) => a + b, 0);

    // Income totals
    const currentIncome = currentMonthTransactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const prevIncome = previousMonthTransactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);

    // 1. Overall spending comparison
    if (prevTotal > 0 && currentTotal > 0) {
      const change = ((currentTotal - prevTotal) / prevTotal) * 100;
      if (change > 20) {
        insightsList.push({
          id: 'spending-up',
          icon: TrendingUp,
          type: 'warning',
          title: 'Spending Increased',
          message: `You spent ${Math.abs(change).toFixed(0)}% more this month compared to last month (${formatCurrency(currentTotal)} vs ${formatCurrency(prevTotal)}).`,
        });
      } else if (change < -20) {
        insightsList.push({
          id: 'spending-down',
          icon: TrendingDown,
          type: 'success',
          title: 'Great Savings!',
          message: `You saved ${Math.abs(change).toFixed(0)}% more this month — that's ${formatCurrency(prevTotal - currentTotal)} less spending! 🎉`,
        });
      }
    }

    // 2. Category-specific insights
    Object.keys(currentTotals).forEach(category => {
      const current = currentTotals[category];
      const prev = prevTotals[category] || 0;
      
      if (prev > 0) {
        const change = ((current - prev) / prev) * 100;
        
        if (change > 40 && current > 500) {
          insightsList.push({
            id: `category-up-${category}`,
            icon: ShoppingCart,
            type: 'warning',
            title: `${category} Spending Up`,
            message: `You spent ${change.toFixed(0)}% more on ${category} this month vs. last month (${formatCurrency(current)} vs ${formatCurrency(prev)}).`,
          });
        }
      }
    });

    // 3. Budget projection insights
    const daysInMonth = differenceInDays(endOfMonth(selectedMonth), startOfMonth(selectedMonth)) + 1;
    const daysPassed = differenceInDays(new Date(), startOfMonth(selectedMonth)) + 1;
    const daysRemaining = daysInMonth - daysPassed;

    Object.keys(budgets).forEach(category => {
      const spent = currentTotals[category] || 0;
      const budget = budgets[category];
      const dailyRate = spent / daysPassed;
      const projectedTotal = dailyRate * daysInMonth;
      const percentUsed = (spent / budget) * 100;

      if (projectedTotal > budget && percentUsed < 100 && daysRemaining > 5) {
        const exceedDate = Math.ceil(budget / dailyRate);
        insightsList.push({
          id: `budget-projection-${category}`,
          icon: AlertTriangle,
          type: 'warning',
          title: 'Budget Alert',
          message: `At this rate, you'll exceed your ${category} budget (${formatCurrency(budget)}) by day ${exceedDate} of this month.`,
        });
      } else if (percentUsed >= 100) {
        insightsList.push({
          id: `budget-exceeded-${category}`,
          icon: AlertTriangle,
          type: 'danger',
          title: `${category} Over Budget!`,
          message: `You've exceeded your ${category} budget by ${formatCurrency(spent - budget)}. Consider reducing spending in this category.`,
        });
      } else if (percentUsed >= 80 && percentUsed < 100) {
        insightsList.push({
          id: `budget-warning-${category}`,
          icon: Target,
          type: 'info',
          title: 'Budget Check',
          message: `You've used ${percentUsed.toFixed(0)}% of your ${category} budget with ${daysRemaining} days left this month.`,
        });
      }
    });

    // 4. Multi-month trend analysis
    Object.keys(currentTotals).forEach(category => {
      const current = currentTotals[category] || 0;
      const prev = prevTotals[category] || 0;
      const twoMonthsAgo = twoMonthsAgoTotals[category] || 0;

      // Check for 3-month increasing trend
      if (current > prev && prev > twoMonthsAgo && twoMonthsAgo > 0) {
        const totalIncrease = ((current - twoMonthsAgo) / twoMonthsAgo) * 100;
        if (totalIncrease > 30) {
          insightsList.push({
            id: `trend-up-${category}`,
            icon: TrendingUp,
            type: 'warning',
            title: 'Rising Trend',
            message: `${category} spending has been increasing for 3 months straight (up ${totalIncrease.toFixed(0)}% overall).`,
          });
        }
      }
    });

    // 5. Savings insight
    const currentSavings = currentIncome - currentTotal;
    const prevSavings = prevIncome - prevTotal;
    
    if (currentSavings > prevSavings && prevSavings > 0) {
      insightsList.push({
        id: 'savings-up',
        icon: PiggyBank,
        type: 'success',
        title: 'Savings Improved!',
        message: `You saved ${formatCurrency(currentSavings - prevSavings)} more than last month — great job! 🎉`,
      });
    } else if (currentSavings > 0 && currentIncome > 0) {
      const savingsRate = (currentSavings / currentIncome) * 100;
      if (savingsRate > 20) {
        insightsList.push({
          id: 'savings-rate',
          icon: CheckCircle,
          type: 'success',
          title: 'Healthy Savings',
          message: `You're saving ${savingsRate.toFixed(0)}% of your income this month. Keep it up!`,
        });
      }
    }

    // 6. Top spending category
    if (Object.keys(currentTotals).length > 0 && currentTotal > 0) {
      const sortedCategories = Object.entries(currentTotals).sort((a, b) => b[1] - a[1]);
      const topCategory = sortedCategories[0];
      const topPercentage = (topCategory[1] / currentTotal) * 100;

      if (topPercentage > 50) {
        insightsList.push({
          id: 'top-category',
          icon: Lightbulb,
          type: 'info',
          title: 'Spending Focus',
          message: `${topCategory[0]} accounts for ${topPercentage.toFixed(0)}% of your spending. Consider setting a budget for this category.`,
        });
      }
    }

    // 7. No spending insight (encouragement)
    if (currentTotal === 0 && allTransactions.length > 0) {
      insightsList.push({
        id: 'no-spending',
        icon: Sparkles,
        type: 'info',
        title: 'Fresh Start',
        message: 'No expenses recorded this month yet. Start tracking to get personalized insights!',
      });
    }

    return insightsList;
  }, [currentMonthTransactions, previousMonthTransactions, twoMonthsAgoTransactions, budgets, selectedMonth, allTransactions, formatCurrency]);

  const getInsightStyle = (type) => {
    const styles = {
      danger: {
        bg: isDark ? '#3D1F1F' : '#FEE2E2',
        border: isDark ? '#DC2626' : '#FCA5A5',
        iconColor: '#DC2626',
      },
      warning: {
        bg: isDark ? '#3D3520' : '#FEF3C7',
        border: isDark ? '#D97706' : '#FCD34D',
        iconColor: '#D97706',
      },
      success: {
        bg: isDark ? '#1F3D2B' : '#D1FAE5',
        border: isDark ? '#059669' : '#6EE7B7',
        iconColor: '#059669',
      },
      info: {
        bg: isDark ? '#1F2937' : '#DBEAFE',
        border: isDark ? '#2563EB' : '#93C5FD',
        iconColor: '#2563EB',
      },
    };
    return styles[type] || styles.info;
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/stats')}
            className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-gray-500 transition-colors"
            data-testid="back-to-stats"
          >
            <ArrowLeft className="w-6 h-6" style={{ color: theme.text }} />
          </button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.text }}>AI Insights</h1>
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              {format(selectedMonth, 'MMMM yyyy')} Analysis
            </p>
          </div>
        </div>

        {/* Insights Grid */}
        {insights.length > 0 ? (
          <div className="space-y-4" data-testid="insights-container">
            {insights.map((insight) => {
              const Icon = insight.icon;
              const style = getInsightStyle(insight.type);
              
              return (
                <div
                  key={insight.id}
                  className="rounded-xl p-4 border-l-4 transition-all hover:shadow-lg"
                  style={{
                    backgroundColor: style.bg,
                    borderLeftColor: theme.accent,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  }}
                  data-testid={`insight-${insight.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${style.iconColor}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: style.iconColor }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1" style={{ color: theme.text }}>
                        {insight.title}
                      </h3>
                      <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
                        {insight.message}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div 
            className="text-center py-16 rounded-xl"
            style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
          >
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ backgroundColor: isDark ? '#2C2C2E' : '#F8F9FA' }}
            >
              <Sparkles className="w-8 h-8" style={{ color: theme.accent }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
              No Insights Yet
            </h3>
            <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>
              Add more transactions to get personalized spending insights
            </p>
            <Button
              onClick={() => navigate('/transactions')}
              style={{ backgroundColor: theme.accent, color: 'white' }}
            >
              Add Transactions
            </Button>
          </div>
        )}

        {/* Refresh hint */}
        {insights.length > 0 && (
          <div className="mt-6 text-center">
            <p className="text-xs flex items-center justify-center gap-2" style={{ color: theme.textSecondary }}>
              <RefreshCw className="w-3 h-3" />
              Insights refresh automatically when you add new transactions
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Insights;
